/**
 * The Leadership report's 16 Oct views (Rusty), below the intake section:
 *
 * - **What we're getting right now**: requests by offering this week vs last
 *   week and since launch (fixed windows; the filters apply, the date range
 *   does not), and the newest requests: number, offering, team, status, age.
 *   No free text.
 * - **Tickets by site and country**: incidents vs requests and open now per
 *   site and country (CustomerLocation, the 23 sites + Ivanti's "Default";
 *   else the requester's site from their other tickets, 20 Oct). One basis:
 *   the map, the country bars and the "No site" tile all count the tickets
 *   raised in the dates, so they add up to the tickets raised; a site with
 *   tickets open now but none raised is listed under the map, not drawn.
 *   "No site" is shown apart, with its channels and why.
 * - **Requesters**: in aggregate only. The report shows no requester names
 *   anywhere, so neither does this: the busiest are "Requester 1, 2, …", a
 *   pseudonym the server makes fresh at every data pull.
 * - **Reassignment trail**: from the Ivanti audit history, as "right team
 *   first time": the top paths, the share that stayed with its first team,
 *   the time at a team before it moved the ticket on, who saved each move,
 *   and every move (filterable, CSV).
 *
 * Every number opens the tickets behind it in the drawer, like the rest of
 * the page; every table is a design-system DataGrid with search, column
 * filters where useful, sorting and its own CSV download.
 */

import { Download, Icon, Search } from "@norican/icons";
import {
  Alert,
  Button,
  Card,
  DataGrid,
  type DataGridColumn,
  type DataGridColumnFilter,
  type DataGridExportHandle,
  FilterSelect,
  Input,
  MiniSparkline,
  Skeleton,
  Statistic,
  Typography,
} from "@norican/react";
import { lazy, type ReactNode, Suspense, useMemo, useRef, useState } from "react";

import { useLanguage, useT } from "@/i18n";
import type { enResources } from "@/i18n/resources";
import type {
  InsightsCountryRow,
  InsightsDwellRow,
  InsightsFirstTeamRow,
  InsightsMoverRow,
  InsightsNewestRequest,
  InsightsOfferingRow,
  InsightsPathRow,
  InsightsRequesterBucket,
  InsightsRequesterSite,
  InsightsResponse,
  InsightsSiteRow,
  InsightsTopRequester,
  InsightsTrailEntry,
} from "@/lib/api";

import { Key, type Part, Ring } from "./insights-charts";
import {
  C,
  changeText,
  CHANNEL_KEYS,
  channelColor,
  durationParts,
  formatValue,
  keyOf,
  NO_SITE,
  offeringValue,
  round1,
  ticketHref,
  ticketLabel,
} from "./insights-helpers";
import type { MapSite } from "./insights-map";
import { LinkNumber, Section, SubTitle } from "./insights-parts";
import type { Open } from "./insights-view";
import {
  ChartCard,
  type Column as ColumnItem,
  Columns,
  Details,
  DwellBars,
  type DwellRowView,
  type FeedItem,
  GoalBars,
  type GoalRow,
  LAST_WEEK,
  Legend,
  MiniStack,
  Muted,
  PairedBars,
  type PairRow,
  RankBars,
  type RankRow,
  RequestFeed,
  Swatch,
  TeamFlow,
} from "./insights-viz";
import { bucketLabel, splitLocated, topOfferings } from "./insights-viz-helpers";

/** The site map, loaded with its country outlines only when the section renders. */
const SiteMap = lazy(() => import("./insights-map").then((m) => ({ default: m.SiteMap })));

/** Newest requests shown as cards (all of them are in "Show details"). */
const FEED_ITEMS = 12;

// ── Shared pieces ───────────────────────────────────────────────────────────

type Live = (typeof enResources)["common"]["leadershipReport"]["live"];
/** The plain (no {{placeholder}}) label keys of each new section. */
type GettingKey = Exclude<keyof Live["getting"], "weekLine" | "more">;
type SitesKey = Exclude<keyof Live["sites"], "withSite" | "noSite" | "withSiteSplit">;
type PeopleKey = Exclude<keyof Live["people"], "requester">;
type MovesKey = Exclude<keyof Live["moves"], "stepOf" | "hours" | "capped">;
type VizKey = Exclude<
  keyof Live["viz"],
  | "dataTableOf"
  | "weeklyFrom"
  | "notOnMap"
  | "openOnly"
  | "noSiteHint"
  | "top10Of"
  | "perPerson"
  | "flowHint"
  | "goalLine"
  | "more"
>;

/** The right-team-first-time goal (the tile's, 85% by default). */
function goalOf(data: InsightsResponse): number {
  return data.tiles.find((tile) => tile.key === "rft")?.goal ?? 85;
}

function useChannelLabel() {
  const { t } = useT("common");
  return (group: string): string => {
    const key = keyOf(CHANNEL_KEYS, group);
    return key ? t(`leadershipReport.live.channel.${key}`) : group;
  };
}

/** A column the grid offers as a FilterSelect above it. */
interface GridFilter {
  columnId: string;
  label: string;
  noun: string;
}

/**
 * A design-system DataGrid with a search box, optional column filters
 * (FilterSelect → `columnFilters`), sorting, and a CSV download of exactly
 * the rows on screen (`exportRef`).
 */
function ReportGrid<Row>({
  label,
  columns,
  rows,
  getRowId,
  searchText,
  filename,
  filters = [],
  tall = false,
}: {
  label: string;
  columns: DataGridColumn<Row>[];
  rows: Row[];
  getRowId: (row: Row) => string;
  searchText: (row: Row) => string;
  filename: string;
  filters?: GridFilter[];
  tall?: boolean;
}) {
  const { t } = useT("common");
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState<Record<string, string[]>>({});
  const exportRef = useRef<DataGridExportHandle>(null);
  const options = useMemo(() => {
    const out: Record<string, string[]> = {};
    for (const f of filters) {
      const column = columns.find((c) => c.id === f.columnId);
      const values = new Set<string>();
      for (const row of rows) {
        const raw = column?.accessor?.(row);
        values.add(raw === null || raw === undefined ? "" : String(raw));
      }
      out[f.columnId] = [...values].sort((a, b) => a.localeCompare(b));
    }
    return out;
  }, [filters, columns, rows]);
  const columnFilters = useMemo<DataGridColumnFilter[]>(
    () =>
      Object.entries(picked)
        .filter(([, values]) => values.length > 0)
        .map(([columnId, values]) => ({ columnId, type: "enum", operator: "in", value: values })),
    [picked],
  );
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2" data-print-hide>
        <div className="w-56">
          <Input
            size="sm"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t("leadershipReport.live.grid.search")}
            aria-label={`${t("leadershipReport.live.grid.search")}: ${label}`}
            startSlot={<Icon icon={Search} size="sm" />}
          />
        </div>
        {filters.map((f) => (
          <FilterSelect
            key={f.columnId}
            size="sm"
            options={(options[f.columnId] ?? []).map((value) => ({
              value,
              label: value || t("leadershipReport.live.moves.notRecorded"),
            }))}
            value={picked[f.columnId] ?? []}
            onValueChange={(values) => setPicked((prev) => ({ ...prev, [f.columnId]: values }))}
            placeholder={f.label}
            noun={f.noun}
            searchPlaceholder={t("leadershipReport.live.filter.search")}
            aria-label={f.label}
          />
        ))}
        <Button
          size="sm"
          variant="outline"
          tone="neutral"
          startIcon={<Icon icon={Download} size="sm" />}
          disabled={rows.length === 0}
          onClick={() => exportRef.current?.downloadCSV(filename)}
        >
          {t("leadershipReport.live.csv")}
        </Button>
      </div>
      <Card
        padding="none"
        className={
          tall ? "max-h-[480px] overflow-auto print:max-h-none print:overflow-visible" : undefined
        }
      >
        <DataGrid
          aria-label={label}
          columns={columns}
          data={rows}
          getRowId={getRowId}
          getRowSearchText={searchText}
          globalFilter={query}
          columnFilters={columnFilters}
          exportRef={exportRef}
          density="compact"
        />
      </Card>
    </div>
  );
}

/** A count that opens its tickets; a plain "0" when there are none. */
function Count({ n, onOpen }: { n: number; onOpen: () => void }) {
  return n > 0 ? <LinkNumber onClick={onOpen}>{n}</LinkNumber> : <span>0</span>;
}

/** A thin incidents (orange) / requests (blue) bar, scaled to `max`. */
function SplitBar({ inc, req, max }: { inc: number; req: number; max: number }) {
  const w = (n: number) => `${max > 0 ? (100 * n) / max : 0}%`;
  return (
    <div className="bg-bg-subtle flex h-2.5 w-full min-w-16 overflow-hidden rounded" aria-hidden>
      <span style={{ width: w(inc), background: C.incident }} />
      <span style={{ width: w(req), background: C.request }} />
    </div>
  );
}

function TicketLink({
  id,
  kind,
  number,
}: {
  id?: string | null;
  kind: string;
  number?: number | null;
}) {
  const href = ticketHref(id);
  const label = ticketLabel(kind, number);
  return href ? (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-accent-fg-subtle font-medium hover:underline"
    >
      {label}
    </a>
  ) : (
    <>{label}</>
  );
}

function useDuration() {
  const { t } = useT("common");
  return (hours: number | null | undefined): string => {
    const d = durationParts(hours);
    if (!d) {
      return "–";
    }
    return d.unit === "h"
      ? t("leadershipReport.live.moves.hours", { value: d.value })
      : t("leadershipReport.live.days", { value: d.value });
  };
}

function useEtTime() {
  const { locale } = useLanguage();
  return (iso: string | null | undefined): string =>
    iso
      ? new Date(iso).toLocaleString(locale, {
          timeZone: "America/New_York",
          day: "numeric",
          month: "short",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "–";
}

/** A New York day ("2026-10-14") as the reader says it ("14 Oct"). */
function useDay() {
  const { locale } = useLanguage();
  return (day: string): string =>
    new Date(`${day}T12:00:00Z`).toLocaleDateString(locale, {
      timeZone: "UTC",
      day: "numeric",
      month: "short",
    });
}

function Stats({ children }: { children: ReactNode }) {
  return (
    <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4 print:grid-cols-4">{children}</div>
  );
}

// ── What we're getting right now ────────────────────────────────────────────

export function GettingSection({ data, open }: { data: InsightsResponse; open: Open }) {
  const { t } = useT("common");
  const age = useDuration();
  const off = data.offerings;
  const T = (key: GettingKey) => t(`leadershipReport.live.getting.${key}`);
  const W = { week: T("week"), last_week: T("lastWeek"), launch: T("launch") } as const;
  const cell = (window: "week" | "last_week" | "launch", row: InsightsOfferingRow): ReactNode => (
    <Count
      n={row[window]}
      onOpen={() =>
        open("offering", offeringValue(window, row.offering), `${row.offering}: ${W[window]}`)
      }
    />
  );
  const columns: DataGridColumn<InsightsOfferingRow>[] = [
    { id: "offering", header: T("offering"), sortable: true, accessor: (r) => r.offering },
    {
      id: "week",
      header: W.week,
      numeric: true,
      sortable: true,
      width: 110,
      accessor: (r) => r.week,
      cell: (r) => cell("week", r),
    },
    {
      id: "last_week",
      header: W.last_week,
      numeric: true,
      sortable: true,
      width: 110,
      accessor: (r) => r.last_week,
      cell: (r) => cell("last_week", r),
    },
    {
      id: "change",
      header: T("change"),
      numeric: true,
      sortable: true,
      width: 90,
      accessor: (r) => r.change,
      cell: (r) => (
        <span className={r.change > 0 ? "font-semibold" : "text-fg-muted"}>
          {changeText(r.change)}
        </span>
      ),
    },
    {
      id: "launch",
      header: W.launch,
      numeric: true,
      sortable: true,
      width: 110,
      accessor: (r) => r.launch,
      cell: (r) => cell("launch", r),
    },
  ];
  const newestColumns: DataGridColumn<InsightsNewestRequest>[] = [
    {
      id: "number",
      header: t("leadershipReport.live.col.ticket"),
      width: 110,
      sortable: true,
      accessor: (r) => r.number ?? null,
      cell: (r) => <TicketLink id={r.ticket_id} kind="Service Request" number={r.number} />,
      exportValue: (r) => ticketLabel("Service Request", r.number),
    },
    { id: "offering", header: T("offering"), sortable: true, accessor: (r) => r.offering },
    {
      id: "team",
      header: t("leadershipReport.live.col.team"),
      sortable: true,
      accessor: (r) => r.team,
    },
    {
      id: "status",
      header: t("leadershipReport.live.col.status"),
      sortable: true,
      accessor: (r) => r.status,
    },
    {
      id: "age_days",
      header: t("leadershipReport.live.col.age"),
      numeric: true,
      sortable: true,
      width: 90,
      accessor: (r) => r.age_days ?? null,
      cell: (r) => age(r.age_days === null || r.age_days === undefined ? null : r.age_days * 24),
    },
  ];
  const V = (key: VizKey) => t(`leadershipReport.live.viz.${key}`);
  const top = topOfferings(off.rows ?? [], 10);
  const pairRows: PairRow[] = top.map((r) => ({
    id: r.offering,
    label: r.offering,
    a: r.week,
    b: r.last_week,
    total: r.launch,
    spark: r.weekly ?? [],
    onA: () => open("offering", offeringValue("week", r.offering), `${r.offering}: ${W.week}`),
    onB: () =>
      open("offering", offeringValue("last_week", r.offering), `${r.offering}: ${W.last_week}`),
    onTotal: () =>
      open("offering", offeringValue("launch", r.offering), `${r.offering}: ${W.launch}`),
  }));
  const newest = off.newest ?? [];
  const feed: FeedItem[] = newest.slice(0, FEED_ITEMS).map((r) => ({
    id: r.ticket_id ?? String(r.number),
    number: <TicketLink id={r.ticket_id} kind="Service Request" number={r.number} />,
    offering: r.offering,
    team: r.team,
    ageDays: r.age_days,
    age: age(r.age_days === null || r.age_days === undefined ? null : r.age_days * 24),
  }));
  return (
    <Section
      title={T("title")}
      definition={t("leadershipReport.live.def.getting")}
      exportAs="getting"
    >
      <Stats>
        {(["week", "last_week", "launch"] as const).map((w) => (
          <Statistic
            key={w}
            size="sm"
            label={`${W[w]} · ${t("leadershipReport.live.kind.request")}`}
            value={off.totals[w]}
            delta={w === "week" ? changeText(off.totals.week - off.totals.last_week) : undefined}
          />
        ))}
      </Stats>
      <Typography variant="caption" tone="muted" className="mb-3 block">
        {t("leadershipReport.live.getting.weekLine", {
          first: off.week_first,
          last: off.week_last,
          prevFirst: off.last_week_first,
          prevLast: off.last_week_last,
        })}
      </Typography>
      {(off.rows ?? []).length === 0 ? (
        <Typography variant="body-sm" tone="muted">
          {T("noRequests")}
        </Typography>
      ) : (
        <div className="grid grid-cols-1 gap-6 2xl:grid-cols-[1.25fr_1fr] print:grid-cols-1">
          <ChartCard
            title={V("topOfferings")}
            definition={t("leadershipReport.live.def.getting")}
            table={{
              columns: [T("offering"), W.week, W.last_week, T("change"), W.launch],
              rows: top.map((r) => [
                r.offering,
                r.week,
                r.last_week,
                changeText(r.change),
                r.launch,
              ]),
            }}
          >
            <Legend>
              <Swatch color={C.request} label={W.week} />
              <Swatch color={LAST_WEEK} label={W.last_week} />
              <span className="inline-flex items-center gap-1.5">
                <MiniSparkline
                  data={[1, 3, 2, 4]}
                  color={C.request}
                  width={22}
                  height={11}
                  aria-hidden
                />
                {off.weeks_first
                  ? `${W.launch} (${t("leadershipReport.live.viz.weeklyFrom", { first: off.weeks_first })})`
                  : W.launch}
              </span>
            </Legend>
            <PairedBars rows={pairRows} labels={{ a: W.week, b: W.last_week, total: W.launch }} />
            {(off.rows ?? []).length > top.length || (off.more ?? 0) > 0 ? (
              <Muted>
                {t("leadershipReport.live.viz.more", {
                  n: (off.rows ?? []).length - top.length + (off.more ?? 0),
                })}{" "}
                · {V("showDetails")}
              </Muted>
            ) : null}
          </ChartCard>
          <ChartCard
            title={T("newest")}
            definition={t("leadershipReport.live.def.getting")}
            table={{
              columns: [
                t("leadershipReport.live.col.ticket"),
                T("offering"),
                t("leadershipReport.live.col.team"),
                t("leadershipReport.live.col.age"),
              ],
              rows: feed.map((f) => [f.number, f.offering, f.team, f.age]),
            }}
          >
            {feed.length === 0 ? (
              <Typography variant="body-sm" tone="muted">
                {T("noRequests")}
              </Typography>
            ) : (
              <RequestFeed items={feed} />
            )}
          </ChartCard>
        </div>
      )}
      <Details>
        <div>
          <SubTitle text={T("byOffering")} definition={t("leadershipReport.live.def.getting")} />
          <ReportGrid
            label={T("byOffering")}
            columns={columns}
            rows={off.rows ?? []}
            getRowId={(r) => r.offering}
            searchText={(r) => r.offering}
            filename="helpdesk-requests-by-offering.csv"
          />
          {(off.more ?? 0) > 0 ? (
            <Typography variant="caption" tone="muted">
              {t("leadershipReport.live.getting.more", { count: off.more ?? 0 })}
            </Typography>
          ) : null}
        </div>
        <div>
          <SubTitle text={T("newest")} definition={t("leadershipReport.live.def.getting")} />
          <ReportGrid
            label={T("newest")}
            columns={newestColumns}
            rows={newest}
            getRowId={(r) => r.ticket_id ?? String(r.number)}
            searchText={(r) => `${r.number ?? ""} ${r.offering} ${r.team} ${r.status}`}
            filename="helpdesk-newest-requests.csv"
          />
        </div>
      </Details>
    </Section>
  );
}

// ── Sites and countries ─────────────────────────────────────────────────────

export function SitesSection({ data, open }: { data: InsightsResponse; open: Open }) {
  const { t } = useT("common");
  const formatDay = useDay();
  const s = data.sites;
  const T = (key: SitesKey) => t(`leadershipReport.live.sites.${key}`);
  const inc = t("leadershipReport.live.kind.incident");
  const req = t("leadershipReport.live.kind.request");
  const maxSite = Math.max(1, ...(s.rows ?? []).map((r) => r.total));
  const maxCountry = Math.max(1, ...(s.countries ?? []).map((r) => r.total));
  function counts<R extends { incidents: number; requests: number; total: number; open: number }>(
    measure: "site" | "country",
    key: (r: R) => string,
    name: (r: R) => string,
    max: number,
  ): DataGridColumn<R>[] {
    const n = (r: R, extra: string | null, value: number, what: string) => (
      <Count
        n={value}
        onOpen={() => open(measure, extra ? `${key(r)}|${extra}` : key(r), `${name(r)}: ${what}`)}
      />
    );
    return [
      {
        id: "incidents",
        header: T("incidents"),
        numeric: true,
        sortable: true,
        width: 100,
        accessor: (r) => r.incidents,
        cell: (r) => n(r, "Incident", r.incidents, inc),
      },
      {
        id: "requests",
        header: T("requests"),
        numeric: true,
        sortable: true,
        width: 100,
        accessor: (r) => r.requests,
        cell: (r) => n(r, "Service Request", r.requests, req),
      },
      {
        id: "total",
        header: T("raised"),
        numeric: true,
        sortable: true,
        width: 90,
        accessor: (r) => r.total,
        cell: (r) => n(r, null, r.total, T("raised")),
      },
      {
        id: "bar",
        header: "",
        width: 140,
        exportable: false,
        cell: (r) => <SplitBar inc={r.incidents} req={r.requests} max={max} />,
      },
      {
        id: "open",
        header: T("open"),
        numeric: true,
        sortable: true,
        width: 90,
        accessor: (r) => r.open,
        cell: (r) => n(r, "open", r.open, T("open")),
      },
    ];
  }
  const siteColumns: DataGridColumn<InsightsSiteRow>[] = [
    { id: "label", header: T("site"), sortable: true, accessor: (r) => r.label },
    { id: "country", header: T("country"), sortable: true, accessor: (r) => r.country },
    ...counts<InsightsSiteRow>(
      "site",
      (r) => r.site,
      (r) => r.label,
      maxSite,
    ),
  ];
  const countryColumns: DataGridColumn<InsightsCountryRow>[] = [
    { id: "country", header: T("country"), sortable: true, accessor: (r) => r.country },
    {
      id: "sites",
      header: T("sites"),
      numeric: true,
      sortable: true,
      width: 70,
      accessor: (r) => r.sites,
    },
    ...counts<InsightsCountryRow>(
      "country",
      (r) => r.country,
      (r) => r.country,
      maxCountry,
    ),
  ];
  const none = s.no_site;
  const V = (key: VizKey) => t(`leadershipReport.live.viz.${key}`);
  // One basis (20 Oct): the map and the bars show what was raised in the dates;
  // a site or country with tickets open now but none raised is listed, not drawn.
  const raisedRows = (s.rows ?? []).filter((r) => r.total > 0);
  const { mapped, unmapped } = splitLocated(raisedRows);
  const mapSites: MapSite[] = mapped.map((r) => ({
    ...r,
    lat: r.lat as number,
    lon: r.lon as number,
  }));
  const countries = s.countries ?? [];
  const raisedCountries = countries.filter((r) => r.total > 0);
  const openOnlyCountries = countries.filter((r) => r.total === 0 && r.open > 0);
  const countryRows: RankRow[] = raisedCountries.slice(0, 12).map((r) => ({
    id: r.country,
    label: r.country,
    onLabel: () => open("country", r.country, `${r.country}: ${T("raised")}`),
    extra: `${T("open")} ${r.open}`,
    parts: [
      {
        id: "inc",
        label: inc,
        value: r.incidents,
        color: C.incident,
        onSelect: () => open("country", `${r.country}|Incident`, `${r.country}: ${inc}`),
      },
      {
        id: "req",
        label: req,
        value: r.requests,
        color: C.request,
        onSelect: () => open("country", `${r.country}|Service Request`, `${r.country}: ${req}`),
      },
    ],
  }));
  const channelLabel = useChannelLabel();
  const noSiteParts: Part[] = (none.channels ?? []).map((c) => ({
    id: c.key,
    label: channelLabel(c.key),
    value: c.count,
    color: channelColor(c.key),
    onSelect: () =>
      open("site", c.value ?? NO_SITE, `${T("noSiteByChannel")}: ${channelLabel(c.key)}`),
  }));
  return (
    <Section title={T("title")} definition={t("leadershipReport.live.def.sites")} exportAs="sites">
      <Typography variant="body-sm" className="mb-1 block font-semibold">
        {t("leadershipReport.live.sites.withSite", {
          pct: formatValue(s.with_site_pct, "pct"),
          sited: s.with_site ?? 0,
          raised: s.raised ?? 0,
        })}
      </Typography>
      <Typography variant="caption" tone="muted" className="mb-3 block">
        {t("leadershipReport.live.sites.withSiteSplit", {
          onTicket: s.on_ticket ?? 0,
          fromRequester: s.from_requester ?? 0,
          noSite: none.total,
        })}
      </Typography>
      {raisedRows.length === 0 ? (
        <Typography variant="body-sm" tone="muted" className="mb-3 block">
          {T("none")}
        </Typography>
      ) : (
        <ChartCard
          title={V("map")}
          definition={t("leadershipReport.live.def.sites")}
          className="mb-5"
          table={{
            columns: [
              T("site"),
              T("country"),
              T("incidents"),
              T("requests"),
              T("raised"),
              T("open"),
            ],
            rows: raisedRows.map((r) => [
              r.label,
              r.country,
              r.incidents,
              r.requests,
              r.total,
              r.open,
            ]),
          }}
        >
          <Legend>
            <Swatch color={C.incident} label={T("incidents")} />
            <Swatch color={C.request} label={T("requests")} />
            <span>{V("mapHint")}</span>
          </Legend>
          <Suspense fallback={<Skeleton className="h-[320px] w-full" />}>
            <SiteMap
              sites={mapSites}
              labels={{
                world: V("mapWorld"),
                europe: V("mapEurope"),
                incidents: T("incidents"),
                requests: T("requests"),
                open: T("open"),
                raised: T("raised"),
                size: V("sizeKey"),
              }}
              onSelect={(site) => open("site", site.site, `${site.label}: ${T("raised")}`)}
            />
          </Suspense>
          {unmapped.length > 0 ? (
            <Muted>
              {t("leadershipReport.live.viz.notOnMap", {
                sites: unmapped.map((r) => `${r.label} (${r.total})`).join(", "),
              })}
            </Muted>
          ) : null}
          {(s.raised_before_only ?? []).length > 0 ? (
            <Muted>
              {t("leadershipReport.live.viz.openOnly", {
                sites: (s.raised_before_only ?? []).map((r) => `${r.label} (${r.open})`).join(", "),
              })}
            </Muted>
          ) : null}
        </ChartCard>
      )}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.5fr_1fr] print:grid-cols-[1.5fr_1fr]">
        <ChartCard
          title={V("byCountryChart")}
          definition={t("leadershipReport.live.def.sites")}
          table={{
            columns: [
              T("country"),
              T("sites"),
              T("incidents"),
              T("requests"),
              T("raised"),
              T("open"),
            ],
            rows: raisedCountries.map((r) => [
              r.country,
              r.sites,
              r.incidents,
              r.requests,
              r.total,
              r.open,
            ]),
          }}
        >
          {countryRows.length === 0 ? (
            <Typography variant="body-sm" tone="muted">
              {T("none")}
            </Typography>
          ) : (
            <>
              <Legend>
                <Swatch color={C.incident} label={T("incidents")} />
                <Swatch color={C.request} label={T("requests")} />
              </Legend>
              <RankBars rows={countryRows} />
            </>
          )}
          {openOnlyCountries.length > 0 ? (
            <Muted>
              {t("leadershipReport.live.viz.openOnly", {
                sites: openOnlyCountries.map((r) => `${r.country} (${r.open})`).join(", "),
              })}
            </Muted>
          ) : null}
        </ChartCard>
        <ChartCard
          title={V("noSiteTile")}
          definition={T("noSiteWhy")}
          table={{
            columns: [T("noSiteByChannel"), T("raised")],
            rows: noSiteParts.map((p) => [p.label, p.value]),
          }}
        >
          <div className="border-border rounded-xl border px-4 py-3">
            <button
              type="button"
              className="text-[40px] leading-tight font-extrabold text-[#495057] hover:underline"
              onClick={() =>
                open(
                  "site",
                  NO_SITE,
                  t("leadershipReport.live.sites.noSite", { count: none.total }),
                )
              }
            >
              {none.total}
            </button>
            <div className="text-fg-muted mb-2 text-[12.5px]">
              {inc} {none.incidents} · {req} {none.requests} · {T("open")} {none.open}
            </div>
            <div className="mb-1 text-[12.5px] font-semibold">{T("noSiteByChannel")}</div>
            <MiniStack parts={noSiteParts} label={T("noSiteByChannel")} />
            <Muted>
              {s.capture_from
                ? t("leadershipReport.live.viz.noSiteHint", {
                    date: formatDay(s.capture_from),
                    before: none.helpdesk_before_capture ?? 0,
                    since: none.helpdesk_since_capture ?? 0,
                  })
                : T("noSiteWhy")}
            </Muted>
          </div>
        </ChartCard>
      </div>
      <Details>
        <div>
          <SubTitle text={T("bySite")} definition={t("leadershipReport.live.def.sites")} />
          <ReportGrid
            label={T("bySite")}
            columns={siteColumns}
            rows={s.rows ?? []}
            getRowId={(r) => r.site}
            searchText={(r) => `${r.label} ${r.country}`}
            filename="helpdesk-tickets-by-site.csv"
            filters={[{ columnId: "country", label: T("country"), noun: T("country") }]}
          />
        </div>
        <div>
          <SubTitle text={T("byCountry")} definition={t("leadershipReport.live.def.sites")} />
          <ReportGrid
            label={T("byCountry")}
            columns={countryColumns}
            rows={countries}
            getRowId={(r) => r.country}
            searchText={(r) => r.country}
            filename="helpdesk-tickets-by-country.csv"
          />
        </div>
      </Details>
    </Section>
  );
}

// ── Requesters (aggregate, no names) ────────────────────────────────────────

export function PeopleSection({ data, open }: { data: InsightsResponse; open: Open }) {
  const { t } = useT("common");
  const p = data.people;
  const T = (key: PeopleKey) => t(`leadershipReport.live.people.${key}`);
  const who = (rank: number) => t("leadershipReport.live.people.requester", { rank });
  const topColumns: DataGridColumn<InsightsTopRequester>[] = [
    {
      id: "rank",
      header: "#",
      numeric: true,
      width: 50,
      sortable: true,
      accessor: (r) => r.rank,
    },
    {
      id: "who",
      header: T("people"),
      accessor: (r) => who(r.rank),
    },
    {
      id: "site",
      header: t("leadershipReport.live.sites.site"),
      sortable: true,
      accessor: (r) => (r.site === NO_SITE ? "" : r.site_label),
    },
    {
      id: "country",
      header: t("leadershipReport.live.sites.country"),
      sortable: true,
      accessor: (r) => (r.site === NO_SITE ? "" : r.country),
    },
    {
      id: "incidents",
      header: t("leadershipReport.live.sites.incidents"),
      numeric: true,
      sortable: true,
      width: 100,
      accessor: (r) => r.incidents,
    },
    {
      id: "requests",
      header: t("leadershipReport.live.sites.requests"),
      numeric: true,
      sortable: true,
      width: 100,
      accessor: (r) => r.requests,
    },
    {
      id: "total",
      header: t("leadershipReport.live.sites.raised"),
      numeric: true,
      sortable: true,
      width: 90,
      accessor: (r) => r.total,
      cell: (r) => <Count n={r.total} onOpen={() => open("requester", r.value, who(r.rank))} />,
    },
    {
      id: "open",
      header: t("leadershipReport.live.sites.open"),
      numeric: true,
      sortable: true,
      width: 90,
      accessor: (r) => r.open,
    },
  ];
  const bucketColumns: DataGridColumn<InsightsRequesterBucket>[] = [
    { id: "key", header: T("ticketsEach"), accessor: (r) => r.key },
    { id: "people", header: T("people"), numeric: true, accessor: (r) => r.people },
    {
      id: "tickets",
      header: T("theirTickets"),
      numeric: true,
      accessor: (r) => r.tickets,
      cell: (r) => (
        <Count
          n={r.tickets}
          onOpen={() => open("requester_bucket", r.value, `${T("ticketsEach")}: ${r.key}`)}
        />
      ),
    },
  ];
  const siteColumns: DataGridColumn<InsightsRequesterSite>[] = [
    {
      id: "label",
      header: t("leadershipReport.live.sites.site"),
      sortable: true,
      accessor: (r) => r.label,
    },
    {
      id: "country",
      header: t("leadershipReport.live.sites.country"),
      sortable: true,
      accessor: (r) => r.country,
    },
    {
      id: "requesters",
      header: T("people"),
      numeric: true,
      sortable: true,
      accessor: (r) => r.requesters,
    },
    {
      id: "tickets",
      header: t("leadershipReport.live.sites.raised"),
      numeric: true,
      sortable: true,
      accessor: (r) => r.tickets,
    },
    {
      id: "per",
      header: T("perPerson"),
      numeric: true,
      sortable: true,
      accessor: (r) => r.per_requester,
      cell: (r) => round1(r.per_requester),
    },
    {
      id: "repeat",
      header: T("repeat"),
      numeric: true,
      sortable: true,
      accessor: (r) => r.repeat,
    },
    { id: "most", header: T("most"), numeric: true, sortable: true, accessor: (r) => r.most },
  ];
  const V = (key: VizKey) => t(`leadershipReport.live.viz.${key}`);
  const ticketsWord = t("leadershipReport.live.unit.tickets");
  const columnsData: ColumnItem[] = (p.buckets ?? []).map((b) => ({
    id: b.key,
    label: bucketLabel(b.key),
    value: b.people,
    sub: `${b.tickets} ${ticketsWord}`,
    onSelect: b.tickets
      ? () => open("requester_bucket", b.value, `${T("ticketsEach")}: ${bucketLabel(b.key)}`)
      : undefined,
  }));
  const share = p.top10_share ?? null;
  const topTickets = p.top10_tickets ?? 0;
  const shareParts: Part[] = [
    { id: "top", label: V("busiest"), value: topTickets, color: C.request },
    {
      id: "rest",
      label: V("everyoneElse"),
      value: Math.max(0, p.tickets - topTickets),
      color: C.before,
    },
  ];
  const siteRows: RankRow[] = (p.sites ?? [])
    .filter((r) => r.site !== NO_SITE)
    .sort((a, b) => b.requesters - a.requesters || a.label.localeCompare(b.label))
    .slice(0, 10)
    .map((r) => ({
      id: r.site,
      label: r.label,
      onLabel: () => open("site", r.site, `${r.label}: ${t("leadershipReport.live.sites.raised")}`),
      extra: t("leadershipReport.live.viz.perPerson", { value: round1(r.per_requester) }),
      parts: [
        {
          id: "people",
          label: T("people"),
          value: r.requesters,
          color: C.request,
          onSelect: () =>
            open("site", r.site, `${r.label}: ${t("leadershipReport.live.sites.raised")}`),
        },
      ],
    }));
  return (
    <Section
      title={T("title")}
      definition={t("leadershipReport.live.def.people")}
      exportAs="people"
    >
      <Typography variant="caption" tone="muted" className="mb-3 block">
        {T("privacy")}
      </Typography>
      {!p.available ? (
        <Alert tone="info" title={T("notYet")} />
      ) : (
        <>
          <Stats>
            <Statistic size="sm" label={T("requesters")} value={p.requesters} />
            <Statistic
              size="sm"
              label={T("perRequester")}
              value={
                p.per_requester === null || p.per_requester === undefined
                  ? "–"
                  : round1(p.per_requester)
              }
            />
            <Statistic size="sm" label={T("top10")} value={formatValue(p.top10_share, "pct")} />
            <Statistic
              size="sm"
              label={t("leadershipReport.live.sites.raised")}
              value={p.tickets}
            />
          </Stats>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.1fr_0.8fr_1.3fr] print:grid-cols-[1.1fr_0.8fr_1.3fr]">
            <ChartCard
              title={V("distribution")}
              definition={t("leadershipReport.live.def.people")}
              table={{
                columns: [T("ticketsEach"), T("people"), T("theirTickets")],
                rows: (p.buckets ?? []).map((b) => [bucketLabel(b.key), b.people, b.tickets]),
              }}
            >
              <Columns items={columnsData} color={C.request} label={V("distribution")} />
              <div className="text-fg-muted mt-1 text-center text-[12px]">{T("ticketsEach")}</div>
            </ChartCard>
            <ChartCard
              title={V("top10Share")}
              definition={t("leadershipReport.live.def.people")}
              table={{
                columns: [V("top10Share"), T("theirTickets")],
                rows: shareParts.map((part) => [part.label, part.value]),
              }}
            >
              <Ring
                pct={share}
                color={C.request}
                big={formatValue(share, "pct")}
                small={t("leadershipReport.live.viz.top10Of", { n: p.tickets })}
                label={V("top10Share")}
              />
              <Key parts={shareParts} total={p.tickets} />
            </ChartCard>
            <ChartCard
              title={V("bySiteChart")}
              definition={t("leadershipReport.live.def.people")}
              table={{
                columns: [
                  t("leadershipReport.live.sites.site"),
                  T("people"),
                  t("leadershipReport.live.sites.raised"),
                  T("perPerson"),
                  T("repeat"),
                  T("most"),
                ],
                rows: (p.sites ?? []).map((r) => [
                  r.label,
                  r.requesters,
                  r.tickets,
                  round1(r.per_requester),
                  r.repeat,
                  r.most,
                ]),
              }}
            >
              {siteRows.length === 0 ? (
                <Typography variant="body-sm" tone="muted">
                  {t("leadershipReport.live.sites.none")}
                </Typography>
              ) : (
                <RankBars rows={siteRows} />
              )}
            </ChartCard>
          </div>
          <Details>
            <div>
              <SubTitle text={T("buckets")} definition={t("leadershipReport.live.def.people")} />
              <ReportGrid
                label={T("buckets")}
                columns={bucketColumns}
                rows={p.buckets ?? []}
                getRowId={(r) => r.key}
                searchText={(r) => r.key}
                filename="helpdesk-requesters-by-volume.csv"
              />
            </div>
            <div>
              <SubTitle text={T("top")} definition={t("leadershipReport.live.def.people")} />
              {(p.top ?? []).length === 0 ? (
                <Typography variant="body-sm" tone="muted">
                  {T("noTop")}
                </Typography>
              ) : (
                <ReportGrid
                  label={T("top")}
                  columns={topColumns}
                  rows={p.top ?? []}
                  getRowId={(r) => String(r.rank)}
                  searchText={(r) => `${who(r.rank)} ${r.site_label} ${r.country}`}
                  filename="helpdesk-busiest-requesters.csv"
                />
              )}
            </div>
            <div>
              <SubTitle text={T("bySite")} definition={t("leadershipReport.live.def.people")} />
              <ReportGrid
                label={T("bySite")}
                columns={siteColumns}
                rows={p.sites ?? []}
                getRowId={(r) => r.site}
                searchText={(r) => `${r.label} ${r.country}`}
                filename="helpdesk-requesters-by-site.csv"
              />
            </div>
          </Details>
        </>
      )}
    </Section>
  );
}

// ── Reassignment trail ──────────────────────────────────────────────────────

export function MovesSection({ data, open }: { data: InsightsResponse; open: Open }) {
  const { t } = useT("common");
  const m = data.moves;
  const dur = useDuration();
  const when = useEtTime();
  const T = (key: MovesKey) => t(`leadershipReport.live.moves.${key}`);
  const notRecorded = T("notRecorded");
  const pathColumns: DataGridColumn<InsightsPathRow>[] = [
    { id: "from", header: T("from"), sortable: true, accessor: (r) => r.from_team },
    { id: "to", header: T("to"), sortable: true, accessor: (r) => r.to_team },
    {
      id: "moves",
      header: T("count"),
      numeric: true,
      sortable: true,
      width: 80,
      accessor: (r) => r.moves,
      cell: (r) => (
        <Count
          n={r.moves}
          onOpen={() =>
            open("path", `${r.from_team}|${r.to_team}`, `${r.from_team} → ${r.to_team}`)
          }
        />
      ),
    },
    {
      id: "tickets",
      header: T("tickets"),
      numeric: true,
      sortable: true,
      width: 80,
      accessor: (r) => r.tickets,
    },
    {
      id: "avg",
      header: T("avgBefore"),
      numeric: true,
      sortable: true,
      width: 140,
      accessor: (r) => r.avg_hours ?? null,
      cell: (r) => dur(r.avg_hours),
    },
  ];
  const firstColumns: DataGridColumn<InsightsFirstTeamRow>[] = [
    { id: "team", header: T("team"), sortable: true, accessor: (r) => r.team },
    {
      id: "tickets",
      header: T("tickets"),
      numeric: true,
      sortable: true,
      width: 80,
      accessor: (r) => r.tickets,
      cell: (r) => (
        <Count n={r.tickets} onOpen={() => open("first_team", r.team, `${T("team")}: ${r.team}`)} />
      ),
    },
    {
      id: "stayed",
      header: T("stayedN"),
      numeric: true,
      sortable: true,
      width: 80,
      accessor: (r) => r.stayed,
      cell: (r) => (
        <Count
          n={r.stayed}
          onOpen={() => open("first_team", `${r.team}|stayed`, `${r.team}: ${T("stayedN")}`)}
        />
      ),
    },
    {
      id: "moved",
      header: T("movedN"),
      numeric: true,
      sortable: true,
      width: 80,
      accessor: (r) => r.moved,
      cell: (r) => (
        <Count
          n={r.moved}
          onOpen={() => open("first_team", `${r.team}|moved`, `${r.team}: ${T("movedN")}`)}
        />
      ),
    },
    {
      id: "pct",
      header: T("stayedPct"),
      numeric: true,
      sortable: true,
      width: 90,
      accessor: (r) => r.stayed_pct ?? null,
      cell: (r) => formatValue(r.stayed_pct, "pct"),
    },
  ];
  const dwellColumns: DataGridColumn<InsightsDwellRow>[] = [
    {
      id: "team",
      header: t("leadershipReport.live.col.team"),
      sortable: true,
      accessor: (r) => r.team,
    },
    {
      id: "out",
      header: T("movesOut"),
      numeric: true,
      sortable: true,
      width: 90,
      accessor: (r) => r.moves_out,
    },
    {
      id: "median",
      header: T("median"),
      numeric: true,
      sortable: true,
      width: 90,
      accessor: (r) => r.median_hours ?? null,
      cell: (r) => dur(r.median_hours),
    },
    {
      id: "avg",
      header: T("average"),
      numeric: true,
      sortable: true,
      width: 90,
      accessor: (r) => r.avg_hours ?? null,
      cell: (r) => dur(r.avg_hours),
    },
  ];
  const moverColumns: DataGridColumn<InsightsMoverRow>[] = [
    { id: "by", header: T("by"), sortable: true, accessor: (r) => r.by || notRecorded },
    {
      id: "moves",
      header: T("count"),
      numeric: true,
      sortable: true,
      width: 80,
      accessor: (r) => r.moves,
      cell: (r) => (
        <Count
          n={r.moves}
          onOpen={() => open("mover", r.by, `${T("by")}: ${r.by || notRecorded}`)}
        />
      ),
    },
    {
      id: "tickets",
      header: T("tickets"),
      numeric: true,
      sortable: true,
      width: 80,
      accessor: (r) => r.tickets,
    },
    {
      id: "top",
      header: T("mostOften"),
      accessor: (r) => `${r.top_from} → ${r.top_to}`,
    },
  ];
  const trailColumns: DataGridColumn<InsightsTrailEntry>[] = [
    {
      id: "at",
      header: T("when"),
      sortable: true,
      width: 130,
      accessor: (r) => r.at ?? null,
      cell: (r) => when(r.at),
      exportValue: (r) => r.at ?? "",
    },
    {
      id: "ticket",
      header: t("leadershipReport.live.col.ticket"),
      sortable: true,
      width: 100,
      accessor: (r) => r.number ?? null,
      cell: (r) => <TicketLink id={r.ticket_id} kind={r.kind} number={r.number} />,
      exportValue: (r) => ticketLabel(r.kind, r.number),
    },
    {
      id: "step",
      header: T("step"),
      width: 70,
      accessor: (r) => t("leadershipReport.live.moves.stepOf", { step: r.step, steps: r.steps }),
    },
    { id: "from", header: T("from"), sortable: true, accessor: (r) => r.from_team },
    { id: "to", header: T("to"), sortable: true, accessor: (r) => r.to_team },
    {
      id: "hours",
      header: T("hoursAt"),
      numeric: true,
      sortable: true,
      width: 110,
      accessor: (r) => r.hours_before ?? null,
      cell: (r) => dur(r.hours_before),
    },
    { id: "by", header: T("by"), sortable: true, accessor: (r) => r.by },
    { id: "owner", header: T("assignedTo"), sortable: true, accessor: (r) => r.owner },
    { id: "now", header: T("teamNow"), sortable: true, accessor: (r) => r.team_now },
  ];
  const V = (key: VizKey) => t(`leadershipReport.live.viz.${key}`);
  const goal = goalOf(data);
  const paths = m.paths ?? [];
  const firstRows: GoalRow[] = (m.first_team ?? []).slice(0, 12).map((r) => ({
    id: r.team,
    label: r.team,
    pct: r.stayed_pct,
    detail: `${T("stayedN")} ${r.stayed} · ${T("movedN")} ${r.moved} · ${T("tickets")} ${r.tickets}`,
    onSelect: () => open("first_team", r.team, `${T("team")}: ${r.team}`),
  }));
  const dwellRows: DwellRowView[] = (m.dwell ?? []).slice(0, 12).map((r) => ({
    ...r,
    id: r.team,
    label: r.team,
    movesOut: r.moves_out,
  }));
  return (
    <Section title={T("title")} definition={t("leadershipReport.live.def.moves")} exportAs="moves">
      <Stats>
        <Statistic size="sm" label={T("checked")} value={m.checked} />
        <Statistic
          size="sm"
          label={T("stayed")}
          value={formatValue(m.stayed_pct, "pct")}
          progress={m.stayed_pct ?? undefined}
          progressTone="success"
        />
        <Statistic
          size="sm"
          label={T("moved")}
          value={<LinkNumber onClick={() => open("moved", null, T("moved"))}>{m.moved}</LinkNumber>}
        />
        <Statistic
          size="sm"
          label={T("perMoved")}
          value={m.per_moved === null || m.per_moved === undefined ? "–" : round1(m.per_moved)}
        />
      </Stats>
      {!m.has_times ? <Alert tone="info" title={T("noTimes")} className="mb-3" /> : null}
      {m.moved === 0 ? (
        <Typography variant="body-sm" tone="muted">
          {T("none")}
        </Typography>
      ) : (
        <div className="flex flex-col gap-6">
          <ChartCard
            title={V("flow")}
            definition={t("leadershipReport.live.def.moves")}
            aside={t("leadershipReport.live.viz.flowHint", { n: paths.length })}
            table={{
              columns: [T("from"), T("to"), T("count"), T("tickets"), T("avgBefore")],
              rows: paths.map((r) => [
                r.from_team,
                r.to_team,
                r.moves,
                r.tickets,
                dur(r.avg_hours),
              ]),
            }}
          >
            <TeamFlow
              paths={paths}
              label={V("flow")}
              sides={{ from: V("fromSide"), to: V("toSide") }}
              onSelect={(from, to) => open("path", `${from}|${to}`, `${from} → ${to}`)}
            />
          </ChartCard>
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2 print:grid-cols-2">
            <ChartCard
              title={V("stayedChart")}
              definition={t("leadershipReport.live.def.rft")}
              table={{
                columns: [T("team"), T("tickets"), T("stayedN"), T("movedN"), T("stayedPct")],
                rows: (m.first_team ?? []).map((r) => [
                  r.team,
                  r.tickets,
                  r.stayed,
                  r.moved,
                  formatValue(r.stayed_pct, "pct"),
                ]),
              }}
            >
              <Legend>
                <Swatch color={C.rft} label={T("stayedPct")} />
                <Swatch
                  color="#212529"
                  label={t("leadershipReport.live.viz.goalLine", { goal })}
                  shape="line"
                />
              </Legend>
              <GoalBars
                rows={firstRows}
                goal={goal}
                goalLabel={t("leadershipReport.live.viz.goalLine", { goal })}
                color={C.rft}
              />
            </ChartCard>
            <ChartCard
              title={V("dwellChart")}
              definition={t("leadershipReport.live.def.moves")}
              table={{
                columns: [
                  t("leadershipReport.live.col.team"),
                  T("movesOut"),
                  T("median"),
                  V("middleHalf"),
                  T("average"),
                ],
                rows: (m.dwell ?? []).map((r) => [
                  r.team,
                  r.moves_out,
                  dur(r.median_hours),
                  `${dur(r.p25_hours)} – ${dur(r.p75_hours)}`,
                  dur(r.avg_hours),
                ]),
              }}
            >
              {m.has_times ? (
                <DwellBars
                  rows={dwellRows}
                  fmt={dur}
                  labels={{ median: V("median"), middle: V("middleHalf"), average: V("average") }}
                />
              ) : (
                <Typography variant="body-sm" tone="muted">
                  {T("noTimes")}
                </Typography>
              )}
            </ChartCard>
          </div>
        </div>
      )}
      {m.moved === 0 ? null : (
        <Details>
          <div>
            <SubTitle text={T("trail")} definition={t("leadershipReport.live.def.moves")} />
            <ReportGrid
              label={T("trail")}
              columns={trailColumns}
              rows={m.trail ?? []}
              getRowId={(r) => `${r.ticket_id ?? r.number}-${r.step}`}
              searchText={(r) =>
                `${ticketLabel(r.kind, r.number)} ${r.number ?? ""} ${r.from_team} ${r.to_team} ${r.by} ${r.owner}`
              }
              filename="helpdesk-move-trail.csv"
              filters={[
                { columnId: "from", label: T("from"), noun: T("from") },
                { columnId: "to", label: T("to"), noun: T("to") },
                { columnId: "by", label: T("by"), noun: T("by") },
              ]}
              tall
            />
            {(m.trail_total ?? 0) > (m.trail ?? []).length ? (
              <Typography variant="caption" tone="muted">
                {t("leadershipReport.live.moves.capped", {
                  shown: (m.trail ?? []).length,
                  total: m.trail_total ?? 0,
                })}
              </Typography>
            ) : null}
          </div>
          <div className="grid grid-cols-1 gap-5 2xl:grid-cols-2">
            <div>
              <SubTitle text={T("paths")} definition={t("leadershipReport.live.def.moves")} />
              <ReportGrid
                label={T("paths")}
                columns={pathColumns}
                rows={paths}
                getRowId={(r) => `${r.from_team}|${r.to_team}`}
                searchText={(r) => `${r.from_team} ${r.to_team}`}
                filename="helpdesk-team-move-paths.csv"
              />
            </div>
            <div>
              <SubTitle text={T("firstTeam")} definition={t("leadershipReport.live.def.rft")} />
              <ReportGrid
                label={T("firstTeam")}
                columns={firstColumns}
                rows={m.first_team ?? []}
                getRowId={(r) => r.team}
                searchText={(r) => r.team}
                filename="helpdesk-first-team.csv"
              />
            </div>
            <div>
              <SubTitle text={T("dwell")} definition={t("leadershipReport.live.def.moves")} />
              <ReportGrid
                label={T("dwell")}
                columns={dwellColumns}
                rows={m.dwell ?? []}
                getRowId={(r) => r.team}
                searchText={(r) => r.team}
                filename="helpdesk-time-before-move.csv"
              />
            </div>
            <div>
              <SubTitle text={T("movers")} definition={t("leadershipReport.live.def.moves")} />
              <ReportGrid
                label={T("movers")}
                columns={moverColumns}
                rows={m.movers ?? []}
                getRowId={(r) => r.by || "-"}
                searchText={(r) => `${r.by} ${r.top_from} ${r.top_to}`}
                filename="helpdesk-who-moved-tickets.csv"
              />
            </div>
          </div>
        </Details>
      )}
    </Section>
  );
}
